import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { campaignService } from '../lib/services/campaignService';
import { useLanguage } from '../contexts/LanguageContext';
import { getCampaignLang } from '../translations/campaign_lang';
import { Campaign, CampaignRecipient } from '../types/campaign';
import {
  ArrowLeftIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  EyeIcon,
  CursorArrowRaysIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import LoadingScreen from '../components/common/LoadingScreen';

const CampaignStats: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { language } = useLanguage();
  const tr = getCampaignLang(language);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [recipients, setRecipients] = useState<CampaignRecipient[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      try {
        const c = await campaignService.getById(id);
        setCampaign(c);
        if (c) {
          const r = await campaignService.getRecipients(id);
          setRecipients(r);
        }
      } catch (error) {
        console.error('Error loading campaign stats:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) return <LoadingScreen />;
  if (!campaign) return <div className="p-6 text-center text-gray-500">Campaign not found</div>;

  const stats = campaign.stats;
  const openRate = stats.delivered > 0 ? ((stats.opened / stats.delivered) * 100).toFixed(1) : '0';
  const clickRate = stats.delivered > 0 ? ((stats.clicked / stats.delivered) * 100).toFixed(1) : '0';
  const deliveryRate = stats.totalRecipients > 0 ? ((stats.delivered / stats.totalRecipients) * 100).toFixed(1) : '0';

  const statCards = [
    { label: tr.campaign.statsTotal, value: stats.totalRecipients, icon: EnvelopeIcon, color: 'from-blue-500 to-cyan-500' },
    { label: tr.campaign.statsSent, value: stats.sent, icon: PaperAirplaneIcon, color: 'from-indigo-500 to-blue-500' },
    { label: tr.campaign.statsDelivered, value: stats.delivered, icon: CheckCircleIcon, color: 'from-green-500 to-emerald-500' },
    { label: tr.campaign.statsOpened, value: stats.opened, icon: EyeIcon, color: 'from-purple-500 to-pink-500' },
    { label: tr.campaign.statsClicked, value: stats.clicked, icon: CursorArrowRaysIcon, color: 'from-amber-500 to-orange-500' },
    { label: tr.campaign.statsBounced, value: stats.bounced, icon: ExclamationTriangleIcon, color: 'from-red-500 to-rose-500' },
    { label: tr.campaign.statsFailed, value: stats.failed, icon: XCircleIcon, color: 'from-gray-500 to-gray-600' },
    { label: tr.campaign.statsUnsubscribed, value: stats.unsubscribed, icon: XCircleIcon, color: 'from-slate-500 to-slate-600' },
  ];

  const rateCards = [
    { label: tr.campaign.deliveryRate, value: `${deliveryRate}%` },
    { label: tr.campaign.openRate, value: `${openRate}%` },
    { label: tr.campaign.clickRate, value: `${clickRate}%` },
  ];

  return (
    <div className="space-y-4 max-w-4xl mx-auto px-2 sm:px-0">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <button
          onClick={() => navigate('/campaigns')}
          className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">{campaign.name}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">{tr.campaign.statsTitle}</p>
        </div>
      </div>

      {/* Rate Cards */}
      <div className="grid grid-cols-3 gap-4">
        {rateCards.map((card, i) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800 text-center"
          >
            <p className="text-3xl font-bold text-blue-700 dark:text-blue-400">{card.value}</p>
            <p className="text-xs text-blue-600 dark:text-blue-500 mt-1">{card.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {statCards.map((stat, i) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700"
            >
              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${stat.color} flex items-center justify-center mb-2`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{stat.label}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Recipients Table */}
      {recipients.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <h3 className="font-bold text-gray-900 dark:text-white">{tr.campaign.recipients}</h3>
          </div>
          <div className="overflow-x-auto max-h-96">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Név</th>
                  <th className="px-4 py-2 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Email</th>
                  <th className="px-4 py-2 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Státusz</th>
                  <th className="px-4 py-2 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Küldve</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {recipients.slice(0, 100).map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/50">
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{r.customerName}</td>
                    <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">{r.email || '—'}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        r.status === 'delivered' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                        r.status === 'opened' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                        r.status === 'failed' || r.status === 'bounced' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                        'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                      {r.sentAt ? new Date(r.sentAt).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignStats;
